// FXAA that tone-maps as it samples (RALLY_GOAL.md §14). three's `fxaa(renderOutput(color))` first
// renders the tone-mapped image into a full-resolution target, then runs FXAA on it: two passes.
// Every render pass costs a command encoder, a pass encoder and their descriptors in Blink's
// garbage-collected heap (~0.8 KB a pass, every frame), and that garbage is what schedules the
// browser's full GCs. Here FXAA reads the scene texture directly and applies the output transform
// (tone mapping + sRGB) to each sample, so edge detection still runs on display-referred luma, in
// one pass.
import type * as THREE from 'three/webgpu';
import FXAANode from 'three/addons/tsl/display/FXAANode.js';

type AnyNode = THREE.Node & { sample(uv: unknown): AnyNode; bias(b: number): AnyNode; uvNode?: AnyNode | null };

class MappedFXAANode extends FXAANode {
  constructor(
    textureNode: THREE.TextureNode,
    private readonly map: (sample: AnyNode) => AnyNode,
  ) {
    super(textureNode);
  }

  override setup(builder: THREE.NodeBuilder): ReturnType<FXAANode['setup']> {
    // FXAANode.setup reads `this.textureNode.bias(-100)`, then only `.uvNode` and `.sample(uv)` of
    // the result (r186): wrap those so every sample goes through `map`.
    const real = this.textureNode as unknown as AnyNode;
    const map = this.map;
    const wrapper = {
      bias: (b: number) => {
        const t = real.bias(b);
        return { uvNode: t.uvNode, sample: (uv: unknown) => map(t.sample(uv)) };
      },
    };
    (this as unknown as { textureNode: unknown }).textureNode = wrapper;
    try {
      return super.setup(builder);
    } finally {
      (this as unknown as { textureNode: unknown }).textureNode = real;
    }
  }
}

/** FXAA over a texture node with `map` (e.g. `renderOutput`) applied to every sample, in one pass. */
export function fxaaMapped(texture: THREE.TextureNode, map: (sample: AnyNode) => AnyNode): THREE.Node {
  return new MappedFXAANode(texture, map) as unknown as THREE.Node;
}
